// PickerSheet — the mobile render of every Dropdown / SelectField /
// InlineSelect (mobile overhaul Phase 0 §0.3.1; mockup §3.2 G, drawn once
// and reused by every later phase). A half sheet of 54px rows: the item's
// phase dot, its label, an optional mono second line (client · number), the
// current choice ticked and tinted. Items carrying `group` sort under mono
// section headers in first-seen order; ungrouped items lead.
//
// The search field is pinned under the header — always when the caller asked
// for it, automatically past eight items. Only an explicitly `searchable`
// picker keeps focus, so the keyboard stays down on a short list you only
// need to tap (mockup §3.2 note G).

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CheckIcon, SearchIcon } from './icons';
import { Sheet, SheetButton, SheetRow, SheetSection } from './Sheet';
import type { DropdownItem } from './primitives';

/** Search appears on its own once the list is longer than this. */
const AUTO_SEARCH_ITEMS = 8;

export function PickerSheet<T extends string>({
  open,
  title,
  items,
  value,
  onSelect,
  onClose,
  searchable,
  placeholder,
  footer,
}: {
  open: boolean;
  title: string;
  items: readonly DropdownItem<T>[];
  value?: T | null;
  onSelect: (v: T) => void;
  onClose: () => void;
  /** Force the search field; it also appears automatically when items.length > 8. */
  searchable?: boolean;
  /** Search placeholder, default "Filter…". */
  placeholder?: string;
  /** With a confirmation footer, picking updates the value without dismissing. */
  footer?: ReactNode;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const showSearch = !!searchable || items.length > AUTO_SEARCH_ITEMS;

  // Reset the filter between openings — a stale query would hide the list.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  // Sheet focuses its first input on open (30ms). That's what we want when
  // the caller asked for search; when the field only appeared because the
  // list is long, hand focus back so the keyboard stays down.
  useEffect(() => {
    if (!open || searchable || !showSearch) return;
    const t = window.setTimeout(() => {
      if (document.activeElement === inputRef.current) inputRef.current?.blur();
    }, 60);
    return () => window.clearTimeout(t);
  }, [open, searchable, showSearch]);

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      q
        ? items.filter(
            (i) => i.label.toLowerCase().includes(q) || (i.sub ?? '').toLowerCase().includes(q),
          )
        : items,
    [items, q],
  );

  // Ungrouped items first, then each group in the order it first appears.
  const sections = useMemo(() => {
    const loose: DropdownItem<T>[] = [];
    const groups: { name: string; items: DropdownItem<T>[] }[] = [];
    for (const item of visible) {
      if (!item.group) {
        loose.push(item);
        continue;
      }
      const found = groups.find((g) => g.name === item.group);
      if (found) found.items.push(item);
      else groups.push({ name: item.group, items: [item] });
    }
    return { loose, groups };
  }, [visible]);
  // The rows in display order — what Enter in the search field picks from.
  const ordered = useMemo(
    () => [...sections.loose, ...sections.groups.flatMap((g) => g.items)],
    [sections],
  );

  if (!open) return null;

  const pick = (v: T) => {
    onSelect(v);
    if (!footer) onClose();
  };

  const row = (item: DropdownItem<T>) => (
    <SheetRow
      key={item.value}
      role="option"
      active={value === item.value}
      icon={item.dotColor ? <span className="dot" style={{ background: item.dotColor }} /> : undefined}
      label={item.label}
      sub={item.sub}
      right={value === item.value ? <CheckIcon width={18} height={18} /> : undefined}
      onSelect={() => pick(item.value)}
    />
  );

  return (
    <Sheet
      open
      onClose={onClose}
      variant="half"
      title={title}
      rightAction={<SheetButton onClick={onClose}>Cancel</SheetButton>}
      pinned={
        showSearch ? (
          <div className="picker-search">
            <SearchIcon width={18} height={18} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder={placeholder ?? 'Filter…'}
              autoComplete="off"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && ordered.length > 0) {
                  e.preventDefault();
                  pick(ordered[0].value);
                }
              }}
            />
          </div>
        ) : undefined
      }
      footer={footer}
      className="sheet-picker"
    >
      <div role="listbox" aria-label={title}>
        {sections.loose.map(row)}
        {sections.groups.map((g) => (
          <div key={g.name} role="group" aria-label={g.name}>
            <SheetSection>{g.name}</SheetSection>
            {g.items.map(row)}
          </div>
        ))}
        {visible.length === 0 && <div className="picker-empty">No matches.</div>}
      </div>
    </Sheet>
  );
}
